import { nextTick, onBeforeUnmount, onMounted, unref, watch } from 'vue'

const activeLayers = []
let lockedLayerCount = 0
let previousBodyOverflow = ''

const isVisible = (element) => Boolean(element?.getClientRects().length)

const getFocusableElements = (root) => [...root.querySelectorAll(
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
)].filter((element) => isVisible(element) && !element.closest('[inert]'))

const findVisible = (selector) => selector
  ? [...document.querySelectorAll(selector)].find(isVisible)
  : null

const lockApplication = () => {
  lockedLayerCount += 1
  if (lockedLayerCount > 1) return
  previousBodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  document.querySelector('[data-app-content]')?.setAttribute('inert', '')
}

const unlockApplication = () => {
  lockedLayerCount = Math.max(0, lockedLayerCount - 1)
  if (lockedLayerCount) return
  document.body.style.overflow = previousBodyOverflow
  document.querySelector('[data-app-content]')?.removeAttribute('inert')
}

export const useModalLayer = ({
  rootRef,
  initialFocusRef,
  onClose,
  fallbackFocusSelector = '[data-primary-action]',
  active = true,
}) => {
  const token = Symbol('modal-layer')
  let isActive = false
  let returnFocus = null

  const isTopLayer = () => activeLayers[activeLayers.length - 1] === token

  const handleKeydown = (event) => {
    const root = rootRef.value
    if (!isActive || !root || !isTopLayer() || !root.contains(event.target)) return
    if (event.target.closest('dialog[open]')) return

    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (event.key !== 'Tab') return

    const focusable = getFocusableElements(root)
    if (!focusable.length) {
      event.preventDefault()
      root.focus()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const activate = async () => {
    if (isActive) return
    returnFocus = document.activeElement instanceof window.HTMLElement ? document.activeElement : null
    isActive = true
    activeLayers.push(token)
    lockApplication()
    document.addEventListener('keydown', handleKeydown)
    await nextTick()

    const root = rootRef.value
    if (!root) return
    const target = initialFocusRef?.value || getFocusableElements(root)[0] || root
    target.focus({ preventScroll: true })
  }

  const deactivate = async ({ restoreFocus = true } = {}) => {
    if (!isActive) return
    isActive = false
    const index = activeLayers.lastIndexOf(token)
    if (index >= 0) activeLayers.splice(index, 1)
    document.removeEventListener('keydown', handleKeydown)
    unlockApplication()
    if (!restoreFocus) return

    await nextTick()
    const target = returnFocus?.isConnected && isVisible(returnFocus)
      ? returnFocus
      : findVisible(fallbackFocusSelector)
    target?.focus({ preventScroll: true })
  }

  const stop = watch(
    () => Boolean(unref(active)),
    (value) => value ? activate() : deactivate(),
    { flush: 'post' },
  )

  onMounted(() => {
    if (unref(active)) activate()
  })
  onBeforeUnmount(() => {
    stop()
    deactivate()
  })

  return { activate, deactivate }
}
